import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PaletteInput } from 'src/components/pages/printType/__generated__/printTypeCreatePaletteMutation.graphql';
import { PaletteColorInput } from 'src/components/pages/printType/__generated__/printTypeUpdatePaletteMutation.graphql';
import { sanitizePaletteColor } from '../helpers';

// Helper function to generate temporary unique IDs for added colors
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const usePaletteColors = (initialPalette: PaletteInput, initialColors: PaletteColorInput[] = [], isSearching: boolean = false) => {
    const [currentPalette, setCurrentPalette] = useState<PaletteInput>(initialPalette);
    const [currentColors, setCurrentColors] = useState<PaletteColorInput[]>(initialColors);
    const [addedColors, setAddedColors] = useState<PaletteColorInput[]>([]);
    const [editedColors, setEditedColors] = useState<PaletteColorInput[]>([]);
    const [deletedColors, setDeletedColors] = useState<string[]>([]);

    // Track whether we've already added the initial color to prevent duplicates
    const hasAddedInitialColor = useRef(false);

    // Add a new color
    const addColor = useCallback(() => {
        const newColor: PaletteColorInput = {
            id: generateTempId(),
            cmykHexCode: '',
            name: '',
            rgbHexCode: '',
            spotColorName: ''
        };
        setAddedColors((prev) => [newColor, ...prev]);
        setCurrentColors((prev) => [newColor, ...prev]);
    }, []);

    const addColors = (colors: PaletteColorInput[]) => {
        const newColors = colors.map((color) => {
            const sanitizedColor = sanitizePaletteColor(color);
            // If the color doesn't have an ID (new color), assign a temporary ID
            if (!sanitizedColor.id) {
                sanitizedColor.id = generateTempId();
            }
            return sanitizedColor;
        });

        setAddedColors((prev) => [...newColors, ...prev]);
        setCurrentColors((prev) => [...newColors, ...prev]);
    };

    const handleCurrentColorsChange = (colors: PaletteColorInput[]) => {
        setCurrentColors(colors);
    };

    // Delete a color
    const deleteColor = (index: number) => {
        const colorToDelete = currentColors[index];

        if (colorToDelete.id && !colorToDelete.id.startsWith('temp_')) {
            // If the color exists in the database (has a real ID), mark it as deleted
            setDeletedColors((prev) => [...prev, colorToDelete.id!]);
        } else if (colorToDelete.id && colorToDelete.id.startsWith('temp_')) {
            // If the color is newly added (has a temp ID), remove it from the addedColors
            setAddedColors((prev) => prev.filter((color) => color.id !== colorToDelete.id));
        }

        // Remove the color from currentColors
        setCurrentColors((prev) => prev.filter((_, i) => i !== index));
    };

    // Update a color
    const updateColor = (index: number, updatedColor: PaletteColorInput, isSingleEditWithSearch: boolean = false) => {
        const existingColor = isSingleEditWithSearch ? editedColors[index] : currentColors[index];

        if (existingColor.id && !existingColor.id.startsWith('temp_')) {
            // If the color exists in the database (has a real ID), mark it as updated
            setEditedColors((prev) => {
                const existingIndex = prev.findIndex((color) => color.id === existingColor.id);
                if (existingIndex !== -1) {
                    const neweditedColors = [...prev];
                    neweditedColors[existingIndex] = updatedColor;
                    return neweditedColors;
                }
                return [...prev, updatedColor];
            });
        } else if (existingColor.id && existingColor.id.startsWith('temp_')) {
            // If the color is newly added (has a temp ID), update it in the addedColors
            setAddedColors((prev) => prev.map((color) => (color.id === existingColor.id ? { ...updatedColor, id: existingColor.id } : color)));
        }

        // Update the color in currentColors
        setCurrentColors((prev) => prev.map((color, i) => (i === index ? { ...updatedColor, id: existingColor.id } : color)));
    };

    // Reset a color (empty all inputs)
    const resetColor = (index: number) => {
        const colorToReset = currentColors[index];

        if (!colorToReset.id) return;

        // Create an empty color with the same ID
        const emptyColor: PaletteColorInput = {
            id: colorToReset.id,
            cmykHexCode: '',
            name: '',
            rgbHexCode: '',
            spotColorName: ''
        };

        if (colorToReset.id.startsWith('temp_')) {
            // If the color is newly added (has a temp ID), reset it in addedColors
            setAddedColors((prev) => prev.map((color) => (color.id === colorToReset.id ? emptyColor : color)));
        } else {
            // If the color exists in the database (has a real ID), remove it from editedColors
            // This effectively reverts any changes made to the color
            setEditedColors((prev) => prev.filter((color) => color.id !== colorToReset.id));
        }

        // Update the color in currentColors
        setCurrentColors((prev) => prev.map((color, i) => (i === index ? emptyColor : color)));
    };

    // Ensure at least one empty color exists (but not when searching)
    useEffect(() => {
        if (currentColors.length === 0 && !isSearching) {
            // Only set the ref on initial load, not on recovery
            if (!hasAddedInitialColor.current) {
                hasAddedInitialColor.current = true;
            }
            addColor();
        }
    }, [currentColors.length, isSearching, addColor]);

    // Remove empty colors
    const removeEmptyColors = () => {
        setAddedColors((prev) =>
            prev.filter((color) => color.name !== '' || color.rgbHexCode !== '' || color.cmykHexCode !== '' || color.spotColorName !== '')
        );
        setCurrentColors((prev) =>
            prev.filter((color) => color.name !== '' || color.rgbHexCode !== '' || color.cmykHexCode !== '' || color.spotColorName !== '')
        );
    };

    const handlePaletteNameChange = (name: string) => {
        setCurrentPalette((prev) => ({
            ...prev,
            name
        }));
    };

    const handlePaletteTypeChange = (paletteTypeID: number) => {
        setCurrentPalette((prev) => ({
            ...prev,
            paletteTypeID
        }));
    };

    // Helper function to find a color by ID in currentColors
    const findColorById = (colorId: string): { color: PaletteColorInput | undefined; index: number } => {
        const index = currentColors.findIndex((color) => color.id === colorId);
        return {
            color: index !== -1 ? currentColors[index] : undefined,
            index
        };
    };

    // Helper function to check if a color is a temporary added color
    const isTemporaryColor = (colorId: string): boolean => {
        return colorId.startsWith('temp_');
    };

    // Helper to filter colors during search, excluding deleted colors and including added colors
    const filterSearchedColors = (colors: PaletteColorInput[], searchTerm: string) => {
        let filteredColors = colors;

        // Include addedColors from the search results by verifying they match the search term(check name and pantone code)
        if (addedColors.length > 0) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            const matchingAddedColors = addedColors.filter(
                (color) =>
                    (color.name && color.name.toLowerCase().includes(lowerSearchTerm)) ||
                    (color.spotColorName && color.spotColorName.toLowerCase().includes(lowerSearchTerm)) ||
                    (color.rgbHexCode && color.rgbHexCode.toLowerCase().includes(lowerSearchTerm))
            );
            filteredColors = [...matchingAddedColors, ...filteredColors];
        }

        // Exclude deletedColors from the search results
        if (deletedColors.length > 0) {
            const deletedColorIds = new Set(deletedColors);
            filteredColors = filteredColors.filter((color) => !color.id || !deletedColorIds.has(color.id));
        }
        return filteredColors;
    };

    // Memoized complete palette with all color arrays
    // This prevents recreating the object on every render
    const completePalette = useMemo((): PaletteInput => {
        return {
            ...currentPalette,
            addedColors,
            editedColors,
            deletedColors
        };
    }, [currentPalette, addedColors, editedColors, deletedColors]);

    return {
        currentPalette: completePalette, // Return the memoized complete palette
        currentColors,
        addedColors,
        editedColors,
        deletedColors,
        setEditedColors,
        setCurrentColors,
        addColor,
        addColors,
        deleteColor,
        updateColor,
        resetColor,
        removeEmptyColors,
        handleCurrentColorsChange,
        handlePaletteNameChange,
        handlePaletteTypeChange,
        findColorById,
        isTemporaryColor,
        filterSearchedColors
    };
};
